import { IUser, User_Factory } from "csd.phoenix.models/User";
import { V3PermissionGroupIds } from "csd.phoenix.models/V3PermissionGroupIds";
import { loadConfig } from "../../services/appConfigService";
import ODataApi from "../../services/api/odata";
import { getCurrentUserAccountEmail } from "../../services/authService";
import AsyncLruCache from "../../utils/AsyncLruCache";
import { assertNonNil } from "../../utils/objectUtils";
import { EFormsAccessLevel } from "../../utils/permissionUtils";
import { refreshCachedPermissionData } from "../permissionGroupsStore";


const _usersCache = new AsyncLruCache<"_", Map<string, IUser>>({ maxItems: 1, expireAfter: 60 * 60 * 1000, promiseFactory: loadUsers })


async function loadUsers() {
  const { oDataUrl } = await loadConfig();
  const api = new ODataApi(oDataUrl, "Users");
  let { value: users } = await api.runSetQuery<IUser>({ method: "GET" });
  const entries = users.map(u => [u.RecordId!, User_Factory.CreateIncoming(u)!] as [string, IUser]);
  return new Map<string, IUser>(entries);
}


async function getCachedMap() {
  return await _usersCache.get("_")
}


export async function getUsers(activeOnly = true) {
  const users = Array.from((await getCachedMap()).values());
  return activeOnly
    ? users.filter(u => u.RecordStatus === 0)
    : users;
}


export async function getUser(userId: string) {
  const map = await getCachedMap();
  return map.get(userId);
}


export function getCurrentUserInfoFromList(users: IUser[], email = getCurrentUserAccountEmail()) {
  if (!email) {
    throw new Error("No logged in user info available.");
  }

  const user = users.find(u => email.toLowerCase() === u.EmailAddress.toLowerCase());
  if (!user) {
    throw new Error("Unable to retrieve logged in user info.");
  }

  return user;
}


export async function getCurrentUserInfo() {
  const users = await getUsers();
  return getCurrentUserInfoFromList(users);
}


export async function getCurrentUserId() {
  const user = await getCurrentUserInfo(); // non-null
  return user.RecordId!;
}


export async function updateUser(userId: string, email: string, accessLevel: EFormsAccessLevel) {
  console.assert(!!userId);
  let user = assertNonNil(await getUser(userId));
  const { oDataUrl } = await loadConfig();
  
  // Check if email changed
  const emailChanged = email.toLowerCase() !== (user.EmailAddress || '').toLowerCase();
  
  // Build UserPermissionGroupMemberships based on access level
  const memberships: any[] = [];
  
  switch (accessLevel) {
    case EFormsAccessLevel.None:
      // No EForms permissions
      break;
    case EFormsAccessLevel.User:
      // Add PatientForms permission
      memberships.push({
        PermissionGroupId: V3PermissionGroupIds.PatientForms,
        UserId: userId
      });
      break;
    case EFormsAccessLevel.Admin:
      // Add PatientFormsAdmin permission
      memberships.push({
        PermissionGroupId: V3PermissionGroupIds.PatientFormsAdmin,
        UserId: userId
      });
      break;
  }
  
  // Update user with email (if changed) and permission memberships
  user = { 
    ...user, 
    EmailAddress: emailChanged ? email : user.EmailAddress,
    CanLogin: emailChanged ? true : user.CanLogin,
    UserPermissionGroupMemberships: memberships
  };
  
  const api = new ODataApi(oDataUrl, "Users");
  const model = { ...user, RecordId: assertNonNil(user.RecordId) };
  await api.runEntityDeepUpdate(model);
  
  // Clear the entire users cache to force reload from server
  _usersCache.clear();
  
  // Refresh permission cache
  await refreshCachedPermissionData();
  
  // Reload from server to get the latest data
  return await getUser(userId);
}


